import { Matrix4, Quaternion, Vector3 } from 'three';
import type { FrameSpec } from '../config/frames';
import { LM, landmarkBounds, lmX, lmY, pixelDistance, type LandmarkBuffer } from '../tracking/landmarks';
import { validatePd } from './Sizing';
import { intrinsicsFromFov, unproject, type CameraIntrinsics } from './CameraIntrinsics';
import { FaceMetrics, TEMPLE_CLEARANCE_MM, templeSpreadDeg, type FaceMetricsSnapshot } from './FaceMetrics';
import { clamp, DepthEstimator, measureIris, type IrisMeasurement } from './IrisScale';
import { headAnglesDeg, mediapipeMatrixToMatrix4, rotationFromMatrix, translationFromMatrix } from './matrix';
import { AnchorFit, CANONICAL_HEAD_WIDTH_MM } from './AnchorFit';

/** Approximate eye rotation centre to iris plane; used only for entered far PD. */
export const EYE_ROTATION_TO_IRIS_MM = 10.5;

/** One tracker output: 478 landmarks + facial transformation matrix. */
export interface FaceObservation {
  /** 478 * 3 floats, normalised image coordinates. */
  landmarks: LandmarkBuffer;
  /** 16 floats (MediaPipe facial transformation matrix) or null. */
  matrix: Float32Array | null;
  /** Dimensions of the image the landmarks are normalised against (aspect must equal the video's). */
  width: number;
  height: number;
}

export interface SolvedFramePose {
  /** Frame origin (bridge) in camera space, mm. */
  position: Vector3;
  /** Frame orientation in camera space (includes pantoscopic tilt). */
  quaternion: Quaternion;
  /** Head rotation only (no tilt). */
  headQuaternion: Quaternion;
  /** Metric depth of the cornea plane, mm. */
  depthMm: number;
  /** Metric depth of the inner-canthi plane (anchor of the face-mesh occluder), mm. */
  canthiDepthMm: number;
  yawDeg: number;
  pitchDeg: number;
  rollDeg: number;
  iris: IrisMeasurement;
  irisReliable: boolean;
  /** Face bounding box in video pixels. */
  bounds: { x: number; y: number; w: number; h: number };
  /** Temple opening angle to apply at the hinges (deg). */
  templeSpreadDeg: number;
  metrics: FaceMetricsSnapshot;
  /** True once the depth estimator has finished its warm-up. */
  calibrated: boolean;
  /** Landmarks the position anchor was fitted over (2 = plain canthi fallback). */
  anchorPoints: number;
  /** Median scatter of the single-landmark anchor estimates (mm): landmark noise of this frame. */
  anchorSpreadMm: number;
}

export interface FramePoseSolverOptions {
  /** Assumed field of view on the long side of the video (deg). */
  longSideFovDeg: number;
  /** Depth of the inner canthi behind the cornea plane (mm). */
  canthusRecessMm: number;
  /** Iris reliability gates. */
  maxYawForIrisDeg: number;
  maxPitchForIrisDeg: number;
  minEyeOpenness: number;
}

export const DEFAULT_SOLVER_OPTIONS: FramePoseSolverOptions = {
  longSideFovDeg: 65,
  canthusRecessMm: 10,
  maxYawForIrisDeg: 35,
  maxPitchForIrisDeg: 25,
  minEyeOpenness: 0.15,
};

/**
 * Computes the 6DoF pose of a frame on the face, in real millimetres.
 *
 *   rotation  ← MediaPipe facial transformation matrix (robust fit over 468 pts)
 *   depth     ← iris diameter (absolute mm) + MediaPipe depth ratio (dynamics)
 *   X, Y      ← back-projection of the inner-canthi midpoint at that depth
 *   origin    ← canthi point + R · (0, anchorOffsetY, canthusRecess + vertexDistance)
 *
 * Why not use MediaPipe's translation directly? Its metric space assumes every
 * face has the canonical model's size, so a 150 mm frame would look the same
 * relative size on a narrow and a wide face. Anchoring X/Y on a landmark and
 * scale on the iris makes the on-screen frame width equal to
 * `widthMm * irisPx / 11.7` pixels in the iris plane — independent of the assumed
 * camera FOV (the 13 mm vertex offset reintroduces a < 1 % dependence).
 */
export class FramePoseSolver {
  readonly options: FramePoseSolverOptions;
  readonly depth = new DepthEstimator();
  readonly metrics = new FaceMetrics();
  private spec: FrameSpec;
  private pdMm: number | null = null;
  private intrinsics: CameraIntrinsics | null = null;
  private lastVideoSize = { width: 0, height: 0 };

  private readonly anchorFit = new AnchorFit();
  private readonly m4 = new Matrix4();
  private readonly headQ = new Quaternion();
  private readonly tiltQ = new Quaternion();
  private readonly translation = new Vector3();
  private readonly forward = new Vector3();
  private readonly canthi = new Vector3();
  private readonly localOffset = new Vector3();
  private readonly xAxis = new Vector3(1, 0, 0);
  private readonly zAxis = new Vector3(0, 0, 1);
  private readonly eyeLineQ = new Quaternion();

  constructor(spec: FrameSpec, options: Partial<FramePoseSolverOptions> = {}) {
    this.spec = spec;
    this.options = { ...DEFAULT_SOLVER_OPTIONS, ...options };
  }

  setFrameSpec(spec: FrameSpec): void {
    this.spec = spec;
  }

  getFrameSpec(): FrameSpec {
    return this.spec;
  }

  /** Total binocular distance PD. Null restores automatic iris calibration. */
  setPupillaryDistance(mm: number | null): void {
    validatePd(mm);
    if (mm === this.pdMm) return;
    this.pdMm = mm;
    this.reset();
  }

  /** Call whenever the video resolution changes (or the FOV assumption is edited). */
  setVideoSize(width: number, height: number): void {
    this.lastVideoSize = { width, height };
    this.intrinsics = intrinsicsFromFov(width, height, this.options.longSideFovDeg);
  }

  setLongSideFov(deg: number): void {
    this.options.longSideFovDeg = deg;
    if (this.lastVideoSize.width > 0) this.setVideoSize(this.lastVideoSize.width, this.lastVideoSize.height);
  }

  getIntrinsics(): CameraIntrinsics | null {
    return this.intrinsics;
  }

  /**
   * Solves the frame pose for one observation. Returns false when no pose can
   * be produced yet (depth not calibrated: the user has not looked at the
   * camera frontally with open eyes yet).
   */
  solve(obs: FaceObservation, tSeconds: number, out: SolvedFramePose): boolean {
    const k = this.intrinsics;
    if (!k) return false;
    const buf = obs.landmarks;
    const W = k.width;
    const H = k.height;

    // 1. Head rotation.
    let mpDepth: number | null = null;
    if (obs.matrix) {
      mediapipeMatrixToMatrix4(obs.matrix, this.m4);
      rotationFromMatrix(this.m4, this.headQ);
      translationFromMatrix(this.m4, this.translation);
      mpDepth = Math.abs(this.translation.z) > 1e-6 ? Math.abs(this.translation.z) : null;
    } else {
      // Fallback without matrix: roll from the eye line, no yaw/pitch.
      const dx = (lmX(buf, LM.LEFT_EYE_OUTER) - lmX(buf, LM.RIGHT_EYE_OUTER)) * W;
      const dy = (lmY(buf, LM.LEFT_EYE_OUTER) - lmY(buf, LM.RIGHT_EYE_OUTER)) * H;
      this.eyeLineQ.setFromAxisAngle(this.zAxis, -Math.atan2(dy, dx));
      this.headQ.copy(this.eyeLineQ);
    }
    const angles = headAnglesDeg(this.headQ);

    // 2. Metric depth of the cornea plane.
    const iris = measureIris(buf, W, H);
    // A small iris (far face, ~1 m on a laptop) is measured at 1–2 px precision:
    // accept it only near-frontal, where foreshortening cannot add to the error.
    const far = iris.meanPx < 18;
    const maxYaw = far ? Math.min(20, this.options.maxYawForIrisDeg) : this.options.maxYawForIrisDeg;
    const maxPitch = far ? Math.min(15, this.options.maxPitchForIrisDeg) : this.options.maxPitchForIrisDeg;
    const irisReliable =
      Math.abs(angles.yaw) <= maxYaw && Math.abs(angles.pitch) <= maxPitch && iris.eyeOpenness >= this.options.minEyeOpenness && iris.meanPx >= 5;
    // PD is only a scale anchor close to frontal, with a known head rotation.
    // MediaPipe continues to carry motion when the wearer turns or blinks.
    const pdPx = pixelDistance(buf, LM.RIGHT_IRIS_CENTER, LM.LEFT_IRIS_CENTER, W, H);
    const pdReliable = irisReliable && obs.matrix !== null && Math.abs(angles.yaw) <= 8 && Math.abs(angles.pitch) <= 8 && pdPx >= 20;
    this.forward.copy(this.xAxis).applyQuaternion(this.headQ);
    const projectedAxis = Math.hypot(this.forward.x, this.forward.y);
    // Camera-fixating pupils project along the eye rotation-centre rays.
    // Convert that depth to the iris plane before calibrating the metric scale.
    const depth = this.depth.update({
      irisPx: iris.meanPx, focalPx: k.f,
      reliable: this.pdMm === null ? irisReliable : pdReliable,
      metricDepthMm: this.pdMm === null ? undefined : (pdReliable ? k.f * this.pdMm * projectedAxis / pdPx - EYE_ROTATION_TO_IRIS_MM : null),
      mediapipeDepth: mpDepth, tSeconds,
    });
    if (depth === null) return false;

    // 3. Anchor: inner-canthi midpoint at the canthi depth — fitted over ~120
    // rigid landmarks when the head rotation is known (AnchorFit), read off
    // the two canthi landmarks otherwise.
    this.forward.copy(this.zAxis).applyQuaternion(this.headQ); // face forward (towards camera when frontal)
    const canthiDepth = depth + this.options.canthusRecessMm * this.forward.z;
    const previous = this.metrics.snapshot();
    if (obs.matrix) {
      const scale = previous.headWidthMm !== null ? clamp(previous.headWidthMm / CANONICAL_HEAD_WIDTH_MM, 0.8, 1.25) : 1;
      const fit = this.anchorFit.solve(buf, k, this.headQ, canthiDepth, scale, this.canthi);
      out.anchorPoints = fit.used;
      out.anchorSpreadMm = fit.spreadMm;
    } else {
      const u = ((lmX(buf, LM.RIGHT_EYE_INNER) + lmX(buf, LM.LEFT_EYE_INNER)) / 2) * W;
      const v = ((lmY(buf, LM.RIGHT_EYE_INNER) + lmY(buf, LM.LEFT_EYE_INNER)) / 2) * H;
      unproject(k, u, v, canthiDepth, this.canthi);
      out.anchorPoints = 2;
      out.anchorSpreadMm = 0;
    }

    // 4. Frame origin: forward from the canthi to the rear of the front, down to the lens centre line.
    const fit = this.spec.fit;
    this.localOffset.set(0, fit.anchorOffsetYMm, this.options.canthusRecessMm + fit.vertexDistanceMm).applyQuaternion(this.headQ);
    out.position.copy(this.canthi).add(this.localOffset);

    // 5. Orientation: head rotation, then pantoscopic tilt about the frame's X axis.
    this.tiltQ.setFromAxisAngle(this.xAxis, (fit.pantoscopicTiltDeg * Math.PI) / 180);
    out.quaternion.copy(this.headQ).multiply(this.tiltQ);
    out.headQuaternion.copy(this.headQ);

    // 6. Anthropometrics (frontal frames only, rolling median).
    this.metrics.update(buf, W, H, depth, k.f, angles.yaw, angles.pitch);
    const metrics = this.metrics.snapshot();

    out.depthMm = depth;
    out.canthiDepthMm = canthiDepth;
    out.yawDeg = angles.yaw;
    out.pitchDeg = angles.pitch;
    out.rollDeg = angles.roll;
    out.iris = iris;
    out.irisReliable = irisReliable;
    out.bounds = landmarkBounds(buf, W, H);
    // Temples run just outside the head occluder (half thickness + margin), so
    // they stay visible along the side of the head in three-quarter views.
    out.templeSpreadDeg = templeSpreadDeg(this.spec.dims.widthMm, metrics.headWidthMm, fit.hingeToEarMm, TEMPLE_CLEARANCE_MM);
    out.metrics = metrics;
    out.calibrated = this.depth.calibrated;
    return true;
  }

  reset(): void {
    this.depth.reset();
    this.metrics.reset();
  }
}

export function createSolvedPose(): SolvedFramePose {
  return {
    position: new Vector3(),
    quaternion: new Quaternion(),
    headQuaternion: new Quaternion(),
    depthMm: 0,
    canthiDepthMm: 0,
    yawDeg: 0,
    pitchDeg: 0,
    rollDeg: 0,
    iris: { rightPx: 0, leftPx: 0, meanPx: 0, eyeOpenness: 0 },
    irisReliable: false,
    bounds: { x: 0, y: 0, w: 0, h: 0 },
    templeSpreadDeg: 0,
    metrics: { pdMm: null, faceWidthMm: null, headWidthMm: null, samples: 0 },
    calibrated: false,
    anchorPoints: 0,
    anchorSpreadMm: 0,
  };
}
